import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, PageShell } from "../components/ui";
import {
 confirmPasswordChange,
 getCurrentUser,
 homePathForRole,
 requestPasswordChangeOtp,
 signOut,
 verifyPasswordChangeOtp,
 type PasswordChangeOtpStatus,
} from "../services/auth";
import { extractErrorMessage } from "../utils/errorMessage";

/* ----------------------------------------------------------------------------
 * Voluntary password-change flow.
 *
 * The page is reachable from the sidebar (`/change-password`) regardless of
 * whether the server has flagged the account as`password_change_required`,
 * so this component does NOT bounce the user away when they aren't locked.
 * Instead it collects three passwords + an OTP and only writes once the OTP
 * is verified. The OTP email is requested lazily when the user clicks
 *"Send OTP"— the sidebar no longer pre-requests it.
 *
 * 1. Fill in current + new + confirm → click"Send OTP"* 2. Enter the 6-digit code from email → click"Verify OTP"* 3. Click"Update Password"(now enabled) → success → role-home redirect
 *
 * A"Logout"button stays pinned to the header as an escape hatch.
 * -------------------------------------------------------------------------- */

type Stage = "request" | "verify" | "reset" | "done";

const OTP_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 8;

const inputClass ="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:bg-slate-50";

const labelClass ="text-sm font-medium text-slate-700";

const primaryButtonClass ="inline-flex items-center justify-center rounded-lg bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60";

const secondaryButtonClass ="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-role-primary shadow-sm transition hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60";

interface BannerProps {
 tone: "error" | "success" | "info";
 children: React.ReactNode;
}

function Banner({ tone, children }: BannerProps) {
 const palette =
 tone ==="error"?"border-red-200 bg-red-50 text-red-700": tone ==="success"?"border-green-200 bg-green-50 text-green-800":"border-slate-200 bg-slate-50 text-slate-700";
 const dot =
 tone ==="error"?"bg-red-500": tone ==="success"?"bg-green-500":"bg-slate-400";
 return (
 <div
 role={tone ==="error"?"alert":"status"}
 className={`flex items-start gap-3 rounded-md border p-3 text-sm ${palette}`}
 >
 <span className={`mt-0.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${dot}`} />
 <div className="flex-1">{children}</div>
 </div>
 );
}

const otpStatusMessage = (status: PasswordChangeOtpStatus): string => {
 switch (status) {
 case"OTP_EXPIRED":
 return"That code has expired. Request a new one to continue.";
 case"INVALID":
 return"That code didn't match. Please try again.";
 case"LOCKED":
 return"Too many incorrect attempts. Request a new code and try again.";
 default:
 return"";
 }
};

export default function ChangePasswordOtp() {
 const navigate = useNavigate();

 const [stage, setStage] = useState<Stage>("request");
 const [alreadyChanged, setAlreadyChanged] = useState<boolean>(false);
 const [checkingStatus, setCheckingStatus] = useState<boolean>(true);
 const [currentPassword, setCurrentPassword] = useState<string>("");
 const [newPassword, setNewPassword] = useState<string>("");
 const [confirmPassword, setConfirmPassword] = useState<string>("");
 const [otp, setOtp] = useState<string>("");
 const [showPassword, setShowPassword] = useState<boolean>(false);

 const [requesting, setRequesting] = useState<boolean>(false);
 const [verifying, setVerifying] = useState<boolean>(false);
 const [submitting, setSubmitting] = useState<boolean>(false);
 const [signingOut, setSigningOut] = useState<boolean>(false);

 const [error, setError] = useState<string | null>(null);
 const [info, setInfo] = useState<string | null>(null);
 const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

 // Auto-focus the OTP input when entering the verify stage.
 const otpInputRef = useRef<HTMLInputElement | null>(null);
 useEffect(() => {
 if (stage ==="verify") {
 otpInputRef.current?.focus();
 }
 }, [stage]);

 // Check on mount if password change is still required or already done
 useEffect(() => {
 let cancelled = false;
 const check = async () => {
 try {
 const { default: API } = await import("../services/API");
 const res = await API.get("/api/auth/change-password/status");
 if (cancelled) return;
 const data = res.data?.data;
 if (data?.alreadyChanged === true || data?.passwordChangeRequired === false) {
 setAlreadyChanged(true);
 }
 } catch {
 // If we can't check, allow the form to show
 } finally {
 if (!cancelled) setCheckingStatus(false);
 }
 };
 void check();
 return () => { cancelled = true; };
 }, []);

 // Cooldown countdown for the "Resend OTP" link.
 useEffect(() => {
 if (cooldownSeconds <= 0) return;
 const timer = window.setTimeout(() => {
 setCooldownSeconds((prev) => Math.max(0, prev - 1));
 }, 1000);
 return () => window.clearTimeout(timer);
 }, [cooldownSeconds]);

 const handleSendOtp = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setInfo(null);

 if (!currentPassword) {
 setError("Please enter your current password.");
 return;
 }

 setRequesting(true);
 try {
 await requestPasswordChangeOtp(currentPassword);
 setOtp("");
 setStage("verify");
 setInfo("A verification code has been emailed to you. It expires in 2 minutes.",
 );
 setCooldownSeconds(30);
 } catch (err) {
 setError(extractErrorMessage(err,"Failed to send verification code."));
 } finally {
 setRequesting(false);
 }
 };

 const handleResendOtp = async () => {
 setError(null);
 setInfo(null);
 setRequesting(true);
 try {
 await requestPasswordChangeOtp(currentPassword);
 setOtp("");
 setInfo("A new verification code has been emailed to you.");
 setCooldownSeconds(30);
 } catch (err) {
 setError(extractErrorMessage(err,"Failed to send verification code."));
 } finally {
 setRequesting(false);
 }
 };

 const handleVerifyOtp = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setInfo(null);

 if (!/^\d{6}$/.test(otp)) {
 setError(`Please enter the ${OTP_LENGTH}-digit code from your email.`);
 return;
 }

 setVerifying(true);
 try {
 const result = await verifyPasswordChangeOtp(otp);
 if (result.status ==="OK") {
 setStage("reset");
 setInfo("Code verified. Click'Update Password'to finish.");
 } else {
 setError(otpStatusMessage(result.status));
 if (result.status ==="LOCKED"|| result.status ==="OTP_EXPIRED") {
 setOtp("");
 setCooldownSeconds(0);
 }
 }
 } catch (err) {
 setError(extractErrorMessage(err,"Failed to verify code."));
 } finally {
 setVerifying(false);
 }
 };

 const handleUpdatePassword = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setInfo(null);

 if (newPassword.length < MIN_PASSWORD_LENGTH) {
 setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
 );
 return;
 }
 if (newPassword !== confirmPassword) {
 setError("New passwords do not match.");
 return;
 }

 setSubmitting(true);
 try {
 await confirmPasswordChange(newPassword);
 setStage("done");
 setCurrentPassword("");
 setNewPassword("");
 setConfirmPassword("");
 setOtp("");
 } catch (err) {
 setError(extractErrorMessage(err,"Failed to update password."));
 } finally {
 setSubmitting(false);
 }
 };

 const handleSignOut = async () => {
 setSigningOut(true);
 try {
 await signOut();
 navigate("/", { replace: true });
 } finally {
 setSigningOut(false);
 }
 };

 const handleGoHome = () => {
 const user = getCurrentUser();
 navigate(homePathForRole(user?.role ?? null, user?.id), { replace: true });
 };

 // Auto-redirect to dashboard 1.5s after successful password change
 useEffect(() => {
 if (stage ==="done") {
 const timer = setTimeout(handleGoHome, 1500);
 return () => clearTimeout(timer);
 }
 }, [stage]);

 return (
 <PageShell maxWidth="max-w-xl">
 <div className="mt-8">
 <Card padded>
 {/* Loading state while checking status */}
 {checkingStatus && (
 <div className="flex flex-col items-center gap-3 py-10">
 <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-emerald-500"/>
 <p className="text-xs font-medium text-slate-500">Checking status…</p>
 </div>
 )}

 {/* Already changed — show success message with go-home option */}
 {!checkingStatus && alreadyChanged && (
 <div className="space-y-5">
 <div>
 <p className="text-xs font-semibold uppercase tracking-wider text-role-primary">
 Account security
 </p>
 <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900">
 Password already changed
 </h1>
 </div>
 <Banner tone="success">
 <p className="font-semibold">Your password has already been updated.</p>
 <p className="mt-1 text-green-700">
 No further action is needed. You can continue using the application.
 </p>
 </Banner>
 <button
 type="button"onClick={handleGoHome}
 className={primaryButtonClass}
 >
 Go to Dashboard
 </button>
 </div>
 )}

 {/* Normal change password flow */}
 {!checkingStatus && !alreadyChanged && (
 <>
 <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
 <div>
 <p className="text-xs font-semibold uppercase tracking-wider text-role-primary">
 Account security
 </p>
 <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900">
 Change your password
 </h1>
 <p className="mt-1 text-sm text-gray-500">
 Enter your current password and verify with the code we email
 you, then set a new password.
 </p>
 </div>
 <button
 type="button"onClick={handleSignOut}
 disabled={signingOut}
 className={secondaryButtonClass}
 >
 {signingOut ?"Signing out…":"Sign out"}
 </button>
 </div>

 {error && (
 <div className="mb-4">
 <Banner tone="error">{error}</Banner>
 </div>
 )}
 {info && !error && (
 <div className="mb-4">
 <Banner tone="info">{info}</Banner>
 </div>
 )}

 {/* Stage 1: collect current + new + confirm, then send OTP. */}
 {stage ==="request"&& (
 <form onSubmit={handleSendOtp} noValidate className="space-y-5">
 <div className="flex flex-col gap-1.5">
 <label htmlFor="currentPassword"className={labelClass}>
 Current password
 </label>
 <div className="relative">
 <input
 id="currentPassword"type={showPassword ?"text":"password"}
 autoComplete="current-password"className={`${inputClass} pr-16`}
 placeholder="Your existing password"value={currentPassword}
 onChange={(e) => setCurrentPassword(e.target.value)}
 disabled={requesting}
 required
 />
 <button
 type="button"onClick={() => setShowPassword((v) => !v)}
 className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 hover:text-role-primary"tabIndex={-1}
 >
 {showPassword ?"Hide":"Show"}
 </button>
 </div>
 </div>

 <div className="flex items-center justify-end gap-3 pt-1">
 <button
 type="submit"disabled={requesting}
 className={primaryButtonClass}
 >
 {requesting ?"Sending code…":"Send OTP"}
 </button>
 </div>
 </form>
 )}

 {/* Stage 2: OTP input + Verify button. */}
 {stage ==="verify"&& (
 <form onSubmit={handleVerifyOtp} noValidate className="space-y-5">
 <div className="flex flex-col gap-1.5">
 <label htmlFor="otp"className={labelClass}>
 Verification code
 </label>
 <input
 id="otp"ref={otpInputRef}
 type="text"inputMode="numeric"autoComplete="one-time-code"pattern={`\\d{${OTP_LENGTH}}`}
 maxLength={OTP_LENGTH}
 className={`${inputClass} text-center text-lg tracking-[0.4em] font-semibold`}
 placeholder="000000"value={otp}
 onChange={(e) => {
 const digitsOnly = e.target.value
 .replace(/\D/g,"")
 .slice(0, OTP_LENGTH);
 setOtp(digitsOnly);
 }}
 disabled={verifying}
 required
 />
 <p className="text-xs text-slate-400">
 Enter the {OTP_LENGTH}-digit code from the email.
 </p>
 </div>

 <div className="flex items-center justify-between gap-3 pt-1">
 <button
 type="button"onClick={handleResendOtp}
 disabled={requesting || cooldownSeconds > 0}
 className="text-sm font-medium text-role-primary transition hover:text-role-primary/80 disabled:cursor-not-allowed disabled:text-slate-400">
 {cooldownSeconds > 0
 ?`Resend code in ${cooldownSeconds}s`: requesting
 ?"Sending…":"Resend code"}
 </button>
 <button
 type="submit"disabled={verifying || otp.length !== OTP_LENGTH}
 className={primaryButtonClass}
 >
 {verifying ?"Verifying…":"Verify OTP"}
 </button>
 </div>
 </form>
 )}

 {/* Stage 3: Update Password button (enabled only after OTP verified). */}
 {stage ==="reset"&& (
 <form
 onSubmit={handleUpdatePassword}
 noValidate
 className="space-y-5">
 <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
 Code verified. You're ready to update your password.
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="newPasswordReset"className={labelClass}>
 New password
 </label>
 <div className="relative">
 <input
 id="newPasswordReset"type={showPassword ?"text":"password"}
 autoComplete="new-password"className={`${inputClass} pr-16`}
 placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
 value={newPassword}
 onChange={(e) => setNewPassword(e.target.value)}
 disabled={submitting}
 required
 />
 <button
 type="button"onClick={() => setShowPassword((v) => !v)}
 className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 hover:text-role-primary"tabIndex={-1}
 >
 {showPassword ?"Hide":"Show"}
 </button>
 </div>
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="confirmPasswordReset"className={labelClass}>
 Confirm new password
 </label>
 <input
 id="confirmPasswordReset"type={showPassword ?"text":"password"}
 autoComplete="new-password"className={inputClass}
 placeholder="Re-enter new password"value={confirmPassword}
 onChange={(e) => setConfirmPassword(e.target.value)}
 disabled={submitting}
 required
 />
 </div>

 <div className="flex items-center justify-end gap-3 pt-1">
 <button
 type="submit"disabled={submitting}
 className={primaryButtonClass}
 >
 {submitting ?"Saving…":"Update Password"}
 </button>
 </div>
 </form>
 )}

 {/* Stage 4: success — redirect home. */}
 {stage ==="done"&& (
 <div className="space-y-4">
 <Banner tone="success">
 <p className="font-semibold">Password updated.</p>
 <p className="mt-1 text-green-700">
 Your password has been changed. You can now continue using
 the app.
 </p>
 </Banner>
 <button
 type="button"onClick={handleGoHome}
 className={primaryButtonClass}
 >
 Continue to dashboard
 </button>
 </div>
 )}
 </>
 )}
 </Card>
 </div>
 </PageShell>
 );
}

